import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Sparkles, UserPlus, Check, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function NewArtistsBanner() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [followedIds, setFollowedIds] = useState(new Set());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const res = await api.get("/artists/recent-joins?limit=5");
        const list = res.data.artists || [];
        setArtists(list);
        setFollowedIds(new Set(list.filter((a) => a.is_followed).map((a) => a.id)));
      } catch (err) {
        console.error(err);
      }
    };
    fetchRecent();
  }, [api]);

  const handleFollow = async (artistId) => {
    try {
      const res = await api.post(`/artists/${artistId}/follow`);
      if (res.data.following) {
        setFollowedIds((prev) => new Set([...prev, artistId]));
      } else {
        setFollowedIds((prev) => {
          const next = new Set(prev);
          next.delete(artistId);
          return next;
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (artists.length === 0 || dismissed) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-[#FF6B35]/10 via-[#7000FF]/10 to-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-3xl p-4 relative overflow-hidden"
      data-testid="new-artists-banner"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF6B35]/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />

      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-[#FF6B35]/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-[#FF6B35]" />
        </div>
        <div>
          <p className="font-heading font-bold text-sm text-foreground">Bienvenue aux nouveaux artistes !</p>
          <p className="text-[10px] text-muted-foreground">Donne-leur de la force, suis-les</p>
        </div>
      </div>

      <div className="space-y-2">
        <AnimatePresence>
          {artists.map((artist, i) => {
            const isFollowed = followedIds.has(artist.id);
            return (
              <motion.div
                key={artist.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-3 bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-2.5 hover:border-[#FF6B35]/30 transition-all cursor-pointer group"
                onClick={() => navigate(`/artist/${artist.id}`)}
                data-testid={`new-artist-${artist.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0 ring-2 ring-[#FF6B35]/20">
                  {artist.avatar_url ? (
                    <img src={artist.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-heading font-bold text-sm">
                      {(artist.name || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-sm text-card-foreground truncate group-hover:text-[#FF6B35] transition-colors">
                    {artist.name}
                    <span className="ml-1.5 text-[9px] font-normal text-[#FF6B35] bg-[#FF6B35]/10 px-1.5 py-0.5 rounded-full">NEW</span>
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {(artist.genres || []).slice(0, 2).join(", ") || "Artiste"}
                    {artist.city ? ` • ${artist.city}` : ""}
                  </p>
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); handleFollow(artist.id); }}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 ${
                    isFollowed
                      ? "bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/30"
                      : "bg-[#FF6B35] text-white hover:scale-105 active:scale-95 shadow-[0_0_12px_rgba(255,107,53,0.3)]"
                  }`}
                  data-testid={`follow-new-artist-${artist.id}`}
                >
                  {isFollowed ? (
                    <><Check className="w-3 h-3" /> Suivi</>
                  ) : (
                    <><UserPlus className="w-3 h-3" /> Soutenir</>
                  )}
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <button
        onClick={() => navigate("/discover")}
        className="flex items-center justify-center gap-1 w-full mt-3 text-xs text-[#FF6B35] font-bold hover:underline"
        data-testid="see-all-artists-btn"
      >
        Voir tous les artistes <ChevronRight className="w-3 h-3" />
      </button>
    </motion.div>
  );
}
